import { computed, effect, inject, Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { Apollo } from "apollo-angular";
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from "keycloak-angular";
import Keycloak from "keycloak-js";
import { Subject } from "rxjs";
import { IAuthService } from "../interfaces/IAuthService";

const fifteenMinutes: number = 1_000 * 60 * 15;

@Injectable({
    providedIn: "root",
})
export class KeycloakAuthService implements IAuthService {
    private readonly apollo = inject(Apollo);
    private readonly keycloakEvent = inject(KEYCLOAK_EVENT_SIGNAL);
    private readonly keycloakService = inject(Keycloak);

    private readonly loginEvent$ = new Subject<void>();
    public readonly onLoginEvent$ = this.loginEvent$.asObservable();

    private readonly logoutEvent$ = new Subject<void>();
    public readonly onLogoutEvent$ = this.logoutEvent$.asObservable();

    private readonly disconnectEvent$ = new Subject<void>();
    public readonly onDisconnectEvent$ = this.disconnectEvent$.asObservable();

    private readonly _isLoggedIn = computed(() => {
        const currentKeycloakEvent = this.keycloakEvent();

        if (currentKeycloakEvent.type === KeycloakEventType.Ready) {
            return typeEventArgs<ReadyArgs>(currentKeycloakEvent.args);
        }

        const loggedEvents = [
            KeycloakEventType.AuthSuccess,
            KeycloakEventType.AuthRefreshSuccess,
        ];
        return loggedEvents.includes(currentKeycloakEvent.type);
    });

    private readonly _isLoggedOut = computed(() => !this.isLoggedIn());

    private readonly _onKeycloakEventType = effect(() => {
        const currentKeycloakEvent = this.keycloakEvent();
        if (currentKeycloakEvent.type === KeycloakEventType.AuthRefreshError) {
            this.logOut();
            console.error("Token failed to refresh");
            throw new Error("Failed to refresh token");
        }
    });

    private readonly _onAccesstokenExpired = effect(() => {
        const currentKeycloakEvent = this.keycloakEvent();
        if (currentKeycloakEvent.type === KeycloakEventType.TokenExpired) {
            this.keycloakService.updateToken(fifteenMinutes)
                .then(() => {
                    // logger?.debug("Token refreshed");
                })
                .catch(() => {
                    console.error("Failed to refresh token");
                });
        }
    });

    init() {}

    isLoggedIn(): boolean {
        return this._isLoggedIn();
    }

    isLoggedOut(): boolean {
        return this._isLoggedOut();
    }

    logOut() {
        this.clearSession();
        this.disconnectEvent$.next();
        this.logoutEvent$.next();

        if (!this.isLoggedIn()) {
            console.warn("User is not logged in. Launch login procedure instead.");
            this.keycloakService.login({
                redirectUri: environment.keycloak.redirectUrl,
            }).catch(e => console.error(e));
            return;
        }

        this.keycloakService.logout({
            redirectUri: environment.keycloak.redirectUrl,
        }).catch(e => console.error(e));
    }

    clearSession(): void {
        this.apollo.client.cache.reset();
        localStorage.clear();
    }
}
